import type React from "react";
import { useEffect, useState, useMemo } from "react";
import { Typography, Box, Divider } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { getLogislySpxOnCallApi } from "../api/logislySpxOnCallApi";
import type { LogislySpxOnCall } from "../types/logislySpxOnCall";
import type { PaginationParam, PaginationResponse } from "../types/paginationResponse";
import LogislySpxOnCallTable from "../components/logislySpxOnCall/LogislySpxOnCallTable";
import LoadingState from "../components/common/LoadingState";
import TablePagination from "../components/common/TablePagination";
import type translations from "../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

const LogislySpxOnCallPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allData, setAllData] = useState<LogislySpxOnCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  // Single pagination state for both sections
  const [paginationState, setPaginationState] = useState<PaginationParam>(() => {
    return {
      page: Number(searchParams.get("page")) || 1,
      pageSize: Number(searchParams.get("pageSize")) || 50,
    };
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch with a large page size to get all data, then filter client-side
        const result = await getLogislySpxOnCallApi({ page: 1, pageSize: 10000 });

        setAllData(result.logislySpxOnCalls);
        setLastUpdatedAt(result.lastUpdatedAt ?? null);
      } catch (err) {
        console.error("Error fetching Logisly SPX OnCall data:", err);
        setError(t("Failed to load Logisly SPX OnCall data" as TranslationKeys));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  // Split data into available and past orders based on lastSeenAt >= lastUpdatedAt - 1 minute
  const { availableOrders, pastOrders, combinedOrders } = useMemo(() => {
    const available: LogislySpxOnCall[] = [];
    const past: LogislySpxOnCall[] = [];

    if (!lastUpdatedAt) {
      // If no lastUpdatedAt, treat all as past orders
      return { availableOrders: [], pastOrders: allData, combinedOrders: allData };
    }

    const lastUpdatedDate = new Date(lastUpdatedAt);
    const cutoffTime = new Date(lastUpdatedDate.getTime() - 60 * 1000); // Subtract 1 minute

    for (const order of allData) {
      const lastSeenAt = new Date(order.lastSeenAt);
      if (lastSeenAt >= cutoffTime) {
        available.push(order);
      } else {
        past.push(order);
      }
    }

    // Combine: available orders first, then past orders
    const combined = [...available, ...past];

    return { availableOrders: available, pastOrders: past, combinedOrders: combined };
  }, [allData, lastUpdatedAt]);

  // Paginate the combined list
  const paginatedOrders = useMemo(() => {
    const start = (paginationState.page - 1) * paginationState.pageSize;
    const end = start + paginationState.pageSize;
    return combinedOrders.slice(start, end);
  }, [combinedOrders, paginationState]);

  // Split paginated results back into available and past for display
  const { paginatedAvailableOrders, paginatedPastOrders } = useMemo(() => {
    const available: LogislySpxOnCall[] = [];
    const past: LogislySpxOnCall[] = [];

    if (!lastUpdatedAt) {
      return { paginatedAvailableOrders: [], paginatedPastOrders: paginatedOrders };
    }

    const lastUpdatedDate = new Date(lastUpdatedAt);
    const cutoffTime = new Date(lastUpdatedDate.getTime() - 60 * 1000); // Subtract 1 minute

    for (const order of paginatedOrders) {
      const lastSeenAt = new Date(order.lastSeenAt);
      if (lastSeenAt >= cutoffTime) {
        available.push(order);
      } else {
        past.push(order);
      }
    }

    return { paginatedAvailableOrders: available, paginatedPastOrders: past };
  }, [paginatedOrders, lastUpdatedAt]);

  // Create single pagination object for combined list
  const pagination: PaginationResponse | null = useMemo(() => {
    if (combinedOrders.length === 0) return null;
    return {
      page: paginationState.page,
      pageSize: paginationState.pageSize,
      total: combinedOrders.length,
      totalPages: Math.ceil(combinedOrders.length / paginationState.pageSize),
      sortBy: "",
      sortOrder: "desc",
    };
  }, [combinedOrders, paginationState]);

  const handlePaginationChange = (newPagination: PaginationParam) => {
    setPaginationState(newPagination);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm");
    } catch {
      return dateString;
    }
  };

  if (loading && !allData.length) {
    return <LoadingState />;
  }

  if (error && !allData.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t("Logisly SPX OnCall" as TranslationKeys)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t("Last Updated" as TranslationKeys)}:{" "}
        {lastUpdatedAt ? formatDate(lastUpdatedAt) : t("never" as TranslationKeys)}
      </Typography>

      {/* Available Orders Section */}
      {paginatedAvailableOrders.length > 0 && (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {t("Available orders" as TranslationKeys)}
          </Typography>
          <LogislySpxOnCallTable
            items={paginatedAvailableOrders}
            loading={loading}
            pagination={null}
            onPaginationChange={undefined}
          />
        </>
      )}

      {/* Past Orders Section */}
      {paginatedPastOrders.length > 0 && (
        <>
          {paginatedAvailableOrders.length > 0 && <Divider sx={{ my: 4 }} />}
          <Typography variant="h5" sx={{ mb: 2 }}>
            {t("Past orders" as TranslationKeys)}
          </Typography>
          <LogislySpxOnCallTable
            items={paginatedPastOrders}
            loading={loading}
            pagination={null}
            onPaginationChange={undefined}
          />
        </>
      )}

      {/* Single pagination control at bottom */}
      {pagination && handlePaginationChange && (
        <TablePagination
          count={pagination.total}
          pagination={{ page: pagination.page, pageSize: pagination.pageSize }}
          onPaginationChange={handlePaginationChange}
          showRowsPerPage={true}
        />
      )}

      {/* Show message if no orders */}
      {paginatedAvailableOrders.length === 0 && paginatedPastOrders.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary">
          {t("No orders found" as TranslationKeys)}
        </Typography>
      )}
    </Box>
  );
};

export default LogislySpxOnCallPage;
